/**
 * Event Icon Component for Timeline V2
 *
 * Wraps EventTypeIcon in appropriate layout for timeline display.
 * Uses centralized icon mappings from lib/crm/utils/icons.
 *
 * Icon sizes (from Figma):
 * - Main events: 26px circle with 16px icon inside
 * - Sub-items: 18px icon
 */

import { Stars02 } from '@untitledui/icons';

import { EventTypeIcon } from '@/lib/crm/utils/icons';

import { TIMELINE_DESIGN_TOKENS } from '../../design-tokens';
import type { EventIconProps } from '../../types/renderer';

/**
 * Event icon component
 *
 * Wraps icon in circle for main events, or shows icon directly for sub-items.
 * Uses centralized EventTypeIcon for consistent icon mappings.
 * Uses enrichment icon (Stars02) when source_type_name is 'ahoy_fullenrich'.
 */
export function EventIcon({
  event,
  event_type_name,
  size = 'main',
  ariaLabel,
}: EventIconProps) {
  const isRiskEntity = event?.entity_type_name === 'ahoy_risk';
  const iconClass = `h-4 w-4 ${isRiskEntity ? 'text-warning-600' : TIMELINE_DESIGN_TOKENS.colors.iconColor}`;

  // Determine icon: enrichment events get Stars02, others get event type icon
  const isEnrichment = event?.source_type_name === 'ahoy_fullenrich';
  // Props type guarantees either event.event_type_name or event_type_name is provided
  const eventTypeName = event?.event_type_name ?? event_type_name ?? 'unknown';
  const icon = isEnrichment ? (
    <Stars02 className={iconClass} />
  ) : (
    <EventTypeIcon eventTypeName={eventTypeName} className={iconClass} />
  );

  if (size === 'sub') {
    // Sub-item: just the icon
    return (
      <div
        className={TIMELINE_DESIGN_TOKENS.spacing.iconSize.sub}
        aria-label={ariaLabel}
      >
        {icon}
      </div>
    );
  }

  // Main event: icon in circle, wrapped in a header-height (28px) band so
  // the circle's centre lines up with the first header line (both at 14px),
  // not the top of the row. The root row stays items-start, so on events
  // with sub-items/preview the icon stays level with the FIRST header line.
  const iconBgClass = isRiskEntity
    ? 'bg-warning-50'
    : TIMELINE_DESIGN_TOKENS.colors.iconBg;
  return (
    <div className='flex min-h-7 items-center shrink-0'>
      <div
        className={`${TIMELINE_DESIGN_TOKENS.spacing.iconSize.main} flex items-center justify-center rounded-full ${iconBgClass} shrink-0`}
        aria-label={ariaLabel}
      >
        {icon}
      </div>
    </div>
  );
}
